/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Tool usage aggregation target kind
 */
export const ToolUsageTargetSummaryTargetKind = {
  Server: "server",
  LocalTools: "local_tools",
  Skill: "skill",
} as const;
/**
 * Tool usage aggregation target kind
 */
export type ToolUsageTargetSummaryTargetKind = OpenEnum<
  typeof ToolUsageTargetSummaryTargetKind
>;

/**
 * Tool usage target type
 */
export const ToolUsageTargetSummaryTargetType = {
  HostedMcpServer: "hosted_mcp_server",
  TunneledMcpServer: "tunneled_mcp_server",
  MetaMcpServer: "meta_mcp_server",
  ShadowMcpServer: "shadow_mcp_server",
  LocalTool: "local_tool",
  Skill: "skill",
} as const;
/**
 * Tool usage target type
 */
export type ToolUsageTargetSummaryTargetType = OpenEnum<
  typeof ToolUsageTargetSummaryTargetType
>;

/**
 * Aggregated tool usage metrics for one target
 */
export type ToolUsageTargetSummary = {
  /**
   * Total number of tool usage events for the target
   */
  eventCount: number;
  /**
   * Number of failed tool usage events for the target
   */
  failureCount: number;
  /**
   * Fraction of completed tool usage events for the target that failed
   */
  failureRate: number;
  /**
   * Number of successful tool usage events for the target
   */
  successCount: number;
  /**
   * Stable target identifier used by filters and chart grouping
   */
  targetId: string;
  /**
   * Tool usage aggregation target kind
   */
  targetKind: ToolUsageTargetSummaryTargetKind;
  /**
   * User-facing label for the target
   */
  targetLabel: string;
  /**
   * Tool usage target type
   */
  targetType: ToolUsageTargetSummaryTargetType;
  /**
   * Number of distinct tools observed for the target
   */
  uniqueTools: number;
};

/** @internal */
export const ToolUsageTargetSummaryTargetKind$inboundSchema: z.ZodMiniType<
  ToolUsageTargetSummaryTargetKind,
  unknown
> = openEnums.inboundSchema(ToolUsageTargetSummaryTargetKind);

/** @internal */
export const ToolUsageTargetSummaryTargetType$inboundSchema: z.ZodMiniType<
  ToolUsageTargetSummaryTargetType,
  unknown
> = openEnums.inboundSchema(ToolUsageTargetSummaryTargetType);

/** @internal */
export const ToolUsageTargetSummary$inboundSchema: z.ZodMiniType<
  ToolUsageTargetSummary,
  unknown
> = z.pipe(
  z.object({
    event_count: z.int(),
    failure_count: z.int(),
    failure_rate: z.number(),
    success_count: z.int(),
    target_id: z.string(),
    target_kind: ToolUsageTargetSummaryTargetKind$inboundSchema,
    target_label: z.string(),
    target_type: ToolUsageTargetSummaryTargetType$inboundSchema,
    unique_tools: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "event_count": "eventCount",
      "failure_count": "failureCount",
      "failure_rate": "failureRate",
      "success_count": "successCount",
      "target_id": "targetId",
      "target_kind": "targetKind",
      "target_label": "targetLabel",
      "target_type": "targetType",
      "unique_tools": "uniqueTools",
    });
  }),
);

export function toolUsageTargetSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ToolUsageTargetSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolUsageTargetSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolUsageTargetSummary' from JSON`,
  );
}
